import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Public routes that should NEVER trigger auto-redirect on 401
const PUBLIC_PATHS = [
  "/login",
  "/signup",
  "/sifre-unuttum",
  "/sifre-sifirla",
];

// Endpoints whose 401 should NEVER trigger a redirect — these are tolerable
// auxiliary calls (e.g. tenant settings) that may transiently fail without
// the user being unauthenticated. The component handles them locally.
const SILENT_401_ENDPOINTS = [
  "/me/tenant-settings",
  "/auth/me",
];

// Endpoints where 401 is an expected credential-check failure rather than a
// session-expired event. Component-level catch SHOULD surface these to the UI.
const CREDENTIAL_CHECK_ENDPOINTS = [
  "/auth/login",
];

// Tek aktif oturum (şartname #3): backend, oturum başka bir cihazdan devralınmışsa
// bu metni içeren 401 döner. Metin backend/auth.py → SESSION_TAKEOVER_DETAIL ile
// eşleşir; yalnızca bir parçasını arıyoruz ki küçük ifade değişiklikleri kırmasın.
const SESSION_TAKEOVER_MARK = "başka bir cihazdan";
// Login ekranının okuyup göstereceği uyarı (sayfa yenilendiği için state taşınamaz).
export const AUTH_NOTICE_KEY = "lastikoteli:auth_notice";

export function takeAuthNotice() {
  try {
    const value = window.sessionStorage.getItem(AUTH_NOTICE_KEY);
    if (value) window.sessionStorage.removeItem(AUTH_NOTICE_KEY);
    return value || "";
  } catch {
    return "";
  }
}

// Suppress noisy "Oturum bulunamadı" 401 toasts; redirect to /login if applicable.
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const reqUrl = error?.config?.url || "";
    const isCredentialCheck = CREDENTIAL_CHECK_ENDPOINTS.some((ep) => reqUrl.includes(ep));
    if (status === 401 && !isCredentialCheck) {
      // mark as silent so component-level catch can ignore
      error.isAuthError = true;
      const path = window.location.pathname;
      const onPublic = PUBLIC_PATHS.some((p) => path.startsWith(p));
      const isSilentEndpoint = SILENT_401_ENDPOINTS.some((ep) => reqUrl.includes(ep));
      // Oturum başka bir yerden devralındıysa kullanıcıya sebebini söyle.
      // NOT: /auth/me da bu bilgiyi taşır (sayfa ilk açılışında oturum düşmüş
      // olabilir), bu yüzden "sessiz uç" ayrımı yapılmadan kaydediliyor.
      const isTakeover =
        typeof detail === "string" && detail.includes(SESSION_TAKEOVER_MARK);
      if (isTakeover) {
        error.isSessionTakeover = true;
        try {
          window.sessionStorage.setItem(AUTH_NOTICE_KEY, detail);
        } catch {
          /* sessionStorage kapalıysa uyarı gösterilemez; akış bozulmaz */
        }
      }
      if (!onPublic && path !== "/login" && (!isSilentEndpoint || isTakeover)) {
        // Defer redirect long enough for React to commit auth state from a
        // successful login response; cancel if user becomes authenticated.
        setTimeout(() => {
          if (window.location.pathname !== "/login") {
            window.location.assign("/login");
          }
        }, 400);
      }
    }
    // Replace generic Turkish auth detail with cleaner message we can hide later
    if (status === 401 && typeof detail === "string" && detail.includes("Oturum")) {
      error.silentMessage = detail;
    }
    // BAKIM MODU: backend firma mutasyonlarını 503 + açıklama ile reddediyor
    // (bkz. backend/maintenance.py). Mesajı tek yerden gösteriyoruz ki her
    // ekranda ayrı ayrı ele almak gerekmesin; tryError/notifyError zaten
    // detail'i kullandığı için ek işleme gerek yok. Bayrak, isteyen bileşenin
    // farklı davranabilmesi için ekleniyor.
    if (status === 503) {
      error.isMaintenance = true;
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Bir hata oluştu, lütfen tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function tryError(e) {
  // Auth errors are handled globally — don't surface to UI
  if (e?.isAuthError) return null;
  return formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Hata";
}

export function showError(toastFn, e) {
  const msg = tryError(e);
  if (msg) toastFn(msg);
}

export function notifyError(e) {
  const msg = tryError(e);
  if (msg) toast.error(msg);
}
